import {Helmet} from 'react-helmet-async'
import {useTranslation} from 'react-i18next'
import {ArrowLeft, Calendar, Clock, Globe, Instagram, Linkedin, Link2, Twitter} from 'lucide-react'
import {Link, Navigate, useParams} from 'react-router-dom'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import {Button} from '@/components/ui/button'
import {Card, CardContent} from '@/components/ui/card'
import {Badge} from '@/components/ui/badge'
import PortableTextRenderer from '@/components/blog/PortableTextRenderer'
import {useAuthor} from '@/hooks/useAuthor'
import {urlForImage} from '@/lib/sanity'
import {blogCategoryLabel} from '@/lib/blogCategories'
import {formatMediumDate} from '@/lib/dates'
import type {AuthorSocialLink} from '@/types/author'
import type {BlogPostSummary} from '@/types/blog'

const iconForLink = (label: string) => {
  const l = label.toLowerCase()
  if (l.includes('linkedin')) return Linkedin
  if (l.includes('instagram')) return Instagram
  if (l === 'x' || l.includes('twitter')) return Twitter
  if (l.includes('portfolio') || l.includes('website') || l.includes('site')) return Globe
  return Link2
}

const SocialIcon = ({link}: {link: AuthorSocialLink}) => {
  const Icon = iconForLink(link.label)
  return (
    <a
      href={link.url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={link.label}
      className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary/80 transition-colors"
    >
      <Icon className="w-4 h-4" />
    </a>
  )
}

const ArticleCard = ({article}: {article: BlogPostSummary}) => {
  const {t, i18n} = useTranslation()
  const src = article.coverImage?.asset
    ? urlForImage(article.coverImage).width(800).fit('max').auto('format').url()
    : null
  return (
    <Link to={`/blog/${article.slug}`} className="group block h-full">
      <Card className="h-full overflow-hidden hover:border-foreground/20 transition-colors">
        <div className="aspect-[16/9] bg-secondary overflow-hidden">
          {src ? (
            <img
              src={src}
              alt={article.coverImage?.alt ?? article.title}
              loading="lazy"
              className="w-full h-full object-cover"
            />
          ) : null}
        </div>
        <CardContent className="p-5 flex flex-col gap-3">
          <div className="flex flex-wrap gap-2">
            {article.categories.map((tag) => (
              <Badge key={tag} variant="secondary" className="text-xs">
                {blogCategoryLabel(t, tag)}
              </Badge>
            ))}
          </div>
          <h3 className="font-display font-semibold text-foreground text-lg leading-snug group-hover:text-foreground/80 transition-colors">
            {article.title}
          </h3>
          <p className="text-sm text-muted-foreground line-clamp-3">{article.excerpt}</p>
          <div className="flex items-center gap-4 text-xs text-muted-foreground mt-auto pt-2">
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" /> {formatMediumDate(article.publishedAt, i18n.language)}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" /> {t('blog.minRead', {count: article.readingMinutes})}
            </span>
          </div>
        </CardContent>
      </Card>
    </Link>
  )
}

const AuthorPage = () => {
  const {t} = useTranslation()
  const {slug} = useParams<{slug: string}>()
  const {data: author, isLoading, isError} = useAuthor(slug)

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-24 max-w-4xl">
          <div className="text-center py-16 text-muted-foreground">{t('authorPage.loading')}</div>
        </main>
        <Footer />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-24 max-w-4xl">
          <div className="text-center py-16 text-muted-foreground">
            {t('authorPage.loadError')}
          </div>
        </main>
        <Footer />
      </div>
    )
  }

  if (!author) {
    return <Navigate to="/authors" replace />
  }

  const pictureSrc = author.picture?.asset
    ? urlForImage(author.picture).width(480).height(480).fit('crop').auto('format').url()
    : null

  const seoTitle = t('authorPage.metaTitle', {name: author.name})

  return (
    <div className="min-h-screen bg-background">
      {/* Title only: AuthorsController's layout owns description/canonical/og/twitter (#674). */}
      <Helmet>
        <title>{seoTitle}</title>
      </Helmet>
      <Header />
      <main className="container mx-auto px-4 py-24 max-w-4xl">
        <Button variant="ghost" size="sm" className="mb-6 -ml-2" asChild>
          <Link to="/authors">
            <ArrowLeft className="w-4 h-4 mr-1" />
            {t('authorPage.allAuthors')}
          </Link>
        </Button>

        <header className="flex flex-col sm:flex-row gap-8 items-center sm:items-start mb-10 pb-10 border-b border-border">
          {pictureSrc ? (
            <img
              src={pictureSrc}
              alt={author.picture.alt ?? author.name}
              className="w-40 h-40 rounded-full object-cover border-4 border-primary/15 shadow-soft shrink-0"
            />
          ) : null}
          <div className="flex-1 text-center sm:text-left">
            <h1 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-2 leading-tight">
              {author.name}
            </h1>
            <p className="text-lg text-muted-foreground mb-4">{author.role}</p>
            <p className="text-base text-foreground/90 mb-5">{author.shortBio}</p>
            {author.socialLinks && author.socialLinks.length > 0 ? (
              <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
                {author.socialLinks.map((link) => (
                  <SocialIcon key={`${link.label}-${link.url}`} link={link} />
                ))}
              </div>
            ) : null}
          </div>
        </header>

        <article className="mb-16">
          <PortableTextRenderer value={author.longBio} />
        </article>

        <section className="pt-10 border-t border-border">
          <p className="text-2xl font-display font-bold text-foreground mb-6">
            {t('authorPage.articlesBy', {name: author.name})}
          </p>
          {author.posts.length === 0 ? (
            <p className="text-muted-foreground">{t('authorPage.noArticles')}</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {author.posts.map((post) => (
                <ArticleCard key={post._id} article={post} />
              ))}
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  )
}

export default AuthorPage
